// Documented professional review for cut-off, accruals and provisions.
// Mirrored in functions/yearClosing/yearClosingPeriodisationModel.ts with parity tests.
const buildYearClosingPeriodisation = ({ state = {}, year, scope, evidence }: any): any => {
  const list = (value: any): any[] => Array.isArray(value) ? value : []
  const canonical = (value: any): any => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object'
    ? Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])])) : value ?? null
  const relevant = (entries: any[]) => list(entries).filter((entry) => {
    const value = String(entry?.date || entry?.invoiceDate || entry?.paymentDate || entry?.period || entry?.createdAt || '')
    return !value || value.slice(0, 4) === String(year) || value.slice(0, 4) === String(Number(year) + 1)
  })
  const basis = JSON.stringify(canonical(window.PlummeYearClosingReviewBasis.normalize({
    year,
    jurisdiction: scope?.jurisdiction,
    legalFormCode: scope?.legalFormCode,
    accountingFramework: scope?.accountingFramework,
    invoices: relevant(state.invoices),
    otherSales: relevant(state.otherSales),
    expenses: relevant(state.expenses),
    payrollRuns: relevant(state.payrollRuns),
    bankPostings: relevant(state.bankReconciliationPostings),
    yearEndEntries: list(state.yearEndEntriesByYear?.[year]),
    periodisationPlans: list(state.periodisationPlans),
    chartAccounts: list(state.chartAccounts),
    reopenedAt: state.yearClosingProcessByYear?.[year]?.reopenedAt,
    invalidatedByPriorYear: state.yearClosingProcessByYear?.[year]?.invalidatedByPriorYear,
  }, state)))
  let h1 = 2166136261; let h2 = 5381
  for (let index = 0; index < basis.length; index++) {
    h1 = Math.imul(h1 ^ basis.charCodeAt(index), 16777619)
    h2 = Math.imul(h2, 33) ^ basis.charCodeAt(index)
  }
  const basisToken = `periodisation-v1:${basis.length}:${h1 >>> 0}:${h2 >>> 0}`
  const supported = scope?.jurisdiction === 'NO'
    && ['partial', 'supported'].includes(scope?.productSupport)
    && Number.isInteger(year) && year >= 2022 && year <= 2200
  const currentEvidence = evidence?.year === year && evidence?.basisToken === basisToken
  const text = (value: any) => Boolean(String(value || '').trim())
  const sectionReady = (section: any, confirmations: string[]) => section
    && typeof section.unresolvedIssues === 'boolean'
    && section.unresolvedIssues === false
    && (section.reviewed === true || (text(section.documentationNote) && confirmations.every((key) => section[key] === true)))
  const unresolved = currentEvidence && [evidence?.periodisation, evidence?.provisions]
    .some((section) => section?.unresolvedIssues === true)
  const ready = currentEvidence
    && sectionReady(evidence.periodisation, ['cutOffReviewed', 'prepaymentsReviewed', 'accrualsReviewed', 'unbilledRevenueReviewed'])
    && sectionReady(evidence.provisions, ['obligationsReviewed', 'estimatesReviewed', 'contingenciesReviewed'])
  const reason = !supported ? 'unsupported'
    : evidence && !currentEvidence ? 'stale'
      : unresolved ? 'unresolved'
        : ready ? 'reconciled' : 'reviewRequired'
  return { year, supported, basisToken, evidence: evidence || null, reason, status: reason === 'reconciled' ? 'ok' : 'todo' }
}

const yearBoundaryDocuments = (state: any, year: number): any[] => {
  const start = `${year}-12-01`, end = `${year + 1}-01-31`
  const collect = (entries: any, type: string) => (Array.isArray(entries) ? entries : []).flatMap((entry: any) => {
    if (['annulled', 'cancelled', 'void', 'draft'].includes(String(entry?.status || '').toLowerCase())) return []
    const date = String(entry?.invoiceDate || entry?.date || '').slice(0, 10)
    if (date < start || date > end) return []
    const reference = String(entry?.invoiceNumber || entry?.supplierInvoiceNumber || entry?.voucherNumber || entry?.id || '').trim()
    if (!reference) return []
    return [{ type, date, reference }]
  })
  return [
    ...collect(state?.invoices, 'sale'),
    ...collect(state?.otherSales, 'sale'),
    ...collect(state?.expenses, 'purchase'),
  ].sort((a, b) => a.date.localeCompare(b.date) || a.reference.localeCompare(b.reference))
}

window.PlummeYearClosingPeriodisation = { build: buildYearClosingPeriodisation, yearBoundaryDocuments }
